// src/hooks/usePushNotifications.js
// Manages the complete FCM push notification lifecycle.
// Extracted from features/alerts/PushNotifications.jsx — hook ≠ feature file.
//
// Returns:
//   status           — PUSH_STATUS enum value
//   isGranted        — boolean shorthand
//   isDenied         — boolean
//   isUnsupported    — boolean
//   topicCount       — number of active FCM subscriptions
//   topics           — string[] of topic names
//   emergency        — { title, body, alertId, alertUrl } | null
//   requestPermission — async () → PUSH_STATUS
//   clearEmergency   — () → void
//   subscribeNearby  — (lat, lon) → Promise<void>

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUserStore, useAlertPrefsStore } from '@store';
import {
  getPushPermissionStatus,
  requestPushPermission,
  setupPushNotifications,
  initForegroundMessages,
  updateTopicSubscriptions,
  subscribeToNearbyParishes,
  flushTopicQueue,
  getSubscribedTopics,
  buildTopicList,
  refreshTokenIfStale,
  PUSH_STATUS,
} from '@services/notifications/push';

export { PUSH_STATUS };

export function usePushNotifications() {
  const zone        = useUserStore((s) => s.zone);
  const zones       = useUserStore((s) => s.zones);
  const alertPrefs  = useAlertPrefsStore((s) => s.types);
  const pushEnabled = useAlertPrefsStore((s) => s.channels.push);

  // Stable zones reference for effect comparisons
  const activeZones = zones.length > 0 ? zones : (zone ? [zone] : []);

  const [status,    setStatus]    = useState(() => getPushPermissionStatus());
  const [topics,    setTopics]    = useState(() => getSubscribedTopics());
  const [swReg,     setSwReg]     = useState(null);
  const [emergency, setEmergency] = useState(null);
  const [banner,    setBanner]    = useState(null);

  const prevPrefsRef  = useRef(alertPrefs);
  const prevZoneRef   = useRef(zone);
  const prevZonesRef  = useRef(JSON.stringify(activeZones));

  // ── SW registration ───────────────────────────────────────────
  useEffect(() => {
    navigator.serviceWorker?.ready.then(setSwReg).catch(() => {});
  }, []);

  // ── Foreground messages (app is open) ────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      setBanner(e.detail);
      // Auto-dismiss non-emergency banners after 8s
      if (e.detail?.severity !== 'extreme' && e.detail?.alertType !== 'evacuation') {
        setTimeout(() => setBanner(null), 8_000);
      }
    };
    window.addEventListener('eos-foreground-message', handler);
    return () => window.removeEventListener('eos-foreground-message', handler);
  }, []);

  // ── Emergency alert from SW push ──────────────────────────────
  useEffect(() => {
    const handler = (e) => setEmergency(e.detail);
    window.addEventListener('na-emergency-alert', handler);
    return () => window.removeEventListener('na-emergency-alert', handler);
  }, []);

  // ── Notification click → navigation ──────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'NAVIGATE_TO' && e.data.url) {
        window.location.href = e.data.url;
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // ── Badge clear on app focus ──────────────────────────────────
  useEffect(() => {
    const clear = () => {
      navigator.clearAppBadge?.().catch(() => {});
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_BADGE' });
    };
    const onVisibility = () => { if (!document.hidden) clear(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', clear);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', clear);
    };
  }, []);

  // ── Full setup (token + topic subscription) ───────────────────
  const setup = useCallback(async () => {
    if (!pushEnabled || !zone || !swReg) return;
    const result = await setupPushNotifications({
      zone,
      zones:          activeZones,
      alertTypePrefs: alertPrefs,
      swRegistration: swReg,
    });
    setStatus(result.status ?? result.reason);
    if (result.topics) setTopics(result.topics);
  }, [pushEnabled, zone, activeZones, alertPrefs, swReg]);

  // Initial setup when SW registration lands
  useEffect(() => {
    if (swReg && status === PUSH_STATUS.GRANTED && pushEnabled) {
      refreshTokenIfStale(swReg).then(() => setup());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swReg]);

  // Zone or zones change → re-subscribe to new parish topics
  useEffect(() => {
    if (status !== PUSH_STATUS.GRANTED || !zone || !pushEnabled) return;
    const zonesKey = JSON.stringify(activeZones);
    if (zone === prevZoneRef.current && zonesKey === prevZonesRef.current) return;
    prevZoneRef.current  = zone;
    prevZonesRef.current = zonesKey;
    setup();
  }, [zone, activeZones, setup]);

  // Pref change → delta subscribe/unsubscribe
  useEffect(() => {
    if (status !== PUSH_STATUS.GRANTED || !zone || !pushEnabled) return;
    const oldPrefs = prevPrefsRef.current;
    if (JSON.stringify(oldPrefs) === JSON.stringify(alertPrefs)) return;
    updateTopicSubscriptions({ zone, zones: activeZones, newPrefs: alertPrefs, oldPrefs });
    prevPrefsRef.current = alertPrefs;
    setTopics(buildTopicList({ zone, zones: activeZones, alertTypePrefs: alertPrefs }));
  }, [alertPrefs, zone, status, pushEnabled]);

  // Flush queued topic subscriptions on reconnect
  useEffect(() => {
    window.addEventListener('online', flushTopicQueue);
    return () => window.removeEventListener('online', flushTopicQueue);
  }, []);

  const requestPermission = useCallback(async () => {
    const result = await requestPushPermission();
    setStatus(result);
    if (result === PUSH_STATUS.GRANTED) await setup();
    return result;
  }, [setup]);

  return {
    status,
    topics,
    topicCount:       topics.length,
    isGranted:        status === PUSH_STATUS.GRANTED,
    isDenied:         status === PUSH_STATUS.DENIED,
    isUnsupported:    status === PUSH_STATUS.UNSUPPORTED,
    emergency,
    requestPermission,
    clearEmergency:   useCallback(() => setEmergency(null), []),
    subscribeNearby:  useCallback((lat, lon) => subscribeToNearbyParishes(lat, lon), []),
  };
}
